import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { chatStream, getSessionMessages, type Attachment, type ChatStreamEvent } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.js'
import {
  clearMessages,
  loadCurrentSessionId,
  loadMessages,
  saveCurrentSessionId,
  saveMessages,
  type StoredMessage,
} from '../lib/chat-storage.js'

import { isIncognitoEnabled } from '../contexts/IncognitoContext.js'

export interface ChatActivity {
  id: string
  kind: 'thinking' | 'searching' | 'memory' | 'planning' | 'tool'
  label: string
  state: 'running' | 'done' | 'error'
}

const TOOL_LABEL: Record<string, { kind: ChatActivity['kind']; label: string }> = {
  search_web: { kind: 'searching', label: 'Stark cherche sur le web' },
  recall: { kind: 'memory', label: 'Stark consulte sa mémoire' },
  remember: { kind: 'memory', label: 'Stark mémorise' },
  schedule_reminder: { kind: 'planning', label: 'Stark planifie un rappel' },
  get_time: { kind: 'thinking', label: 'Stark vérifie l’heure' },
  dispatch_to_constellation: { kind: 'planning', label: 'Stark délègue' },
}

export interface UseChatResult {
  messages: StoredMessage[]
  sessionId: string | null
  isSending: boolean
  error: string | null
  /** Live activity (recall + tool calls + thinking) for the in-flight turn. */
  activities: ChatActivity[]
  send: (message: string, attachments?: Attachment[]) => void
  reset: () => void
  clearError: () => void
  /** Hydrate a past session into the active chat so the next `send` continues
   *  it. Fetches the message history from the server and stores it locally so
   *  the conversation behaves identically to one started in the current tab. */
  loadSession: (sessionId: string) => Promise<void>
}


export function useChat(): UseChatResult {
  const [sessionId, setSessionId] = useState<string | null>(() => loadCurrentSessionId())
  const [messages, setMessages] = useState<StoredMessage[]>(() =>
    loadMessages(loadCurrentSessionId()),
  )
  const [error, setError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [activities, setActivities] = useState<ChatActivity[]>([])

  const abortRef = useRef<AbortController | null>(null)
  const queryClient = useQueryClient()
  const { user, isLoading: authLoading } = useAuth()

  useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  // One-shot rehydrate of the session that was in localStorage at app
  // STARTUP, after auth settles. Pulls server-persisted attachment URLs
  // (/api/uploads/...) so previews come back on hard refresh.
  //
  // Locked to the initial sessionId via a ref so the effect doesn't refire
  // when the SSE 'session' event sets a NEW sessionId mid-conversation —
  // that would refetch a half-persisted message and clobber the optimistic
  // user bubble (notably wiping the in-memory image dataUrl shown right
  // after send).
  const initialSessionIdRef = useRef<string | null>(loadCurrentSessionId())
  const hydratedRef = useRef(false)
  useEffect(() => {
    if (hydratedRef.current) return
    const sid = initialSessionIdRef.current
    if (!sid) return
    if (authLoading || !user) return
    hydratedRef.current = true
    void getSessionMessages(sid).then((remote) => {
      const stored: StoredMessage[] = remote
        .filter((m): m is typeof m & { role: 'user' | 'assistant' } =>
          m.role === 'user' || m.role === 'assistant',
        )
        .map((m) => {
          const meta = (m.metadata ?? {}) as { attachments?: Array<{ name: string; mime: string; size: number; url?: string }> }
          const atts = Array.isArray(meta.attachments) ? meta.attachments : []
          const attachments = atts.map((a) => ({
            name: a.name,
            mime: a.mime,
            size: a.size,
            ...(a.url ? { dataUrl: a.url } : {}),
          }))
          return {
            role: m.role,
            content: m.content,
            ...(attachments.length > 0 ? { attachments } : {}),
          }
        })
      setMessages(stored)
      saveMessages(sid, stored)
    }).catch(() => { /* offline / 404 — keep the localStorage view */ })
  }, [user, authLoading])

  const send = useCallback(
    (message: string, attachments?: Attachment[]) => {
      const trimmed = message.trim()
      const hasAttachments = !!attachments && attachments.length > 0
      if (!trimmed && !hasAttachments) return
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl

      setError(null)
      setActivities([])
      setIsSending(true)

      const userMsg: StoredMessage = {
        role: 'user',
        content: trimmed,
        ...(hasAttachments
          ? {
              attachments: attachments!.map((a) => ({
                name: a.name,
                mime: a.mime,
                size: a.size,
                // In-memory only: lets ChatMessage render a real preview
                // of the image the user just sent. Stripped before
                // localStorage by saveMessages.
                ...(a.mime.startsWith('image/')
                  ? { dataUrl: `data:${a.mime};base64,${a.data}` }
                  : {}),
              })),
            }
          : {}),
      }
      setMessages((prev) => {
        const next = [...prev, userMsg]
        if (sessionId) saveMessages(sessionId, next)
        return next
      })

      // High-level UX: only one activity visible at a time. Replace it as the
      // agent moves through phases (thinking ↔ searching ↔ thinking…). We
      // intentionally hide low-level details like recall, iteration numbers,
      // durations, and raw queries — the user just wants to know what Stark
      // is *doing right now*.
      let currentSessionId = sessionId
      const showTool = (toolName: string): void => {
        const meta = TOOL_LABEL[toolName] ?? { kind: 'tool', label: `Stark utilise ${toolName}` }
        setActivities([{ id: `tool-${toolName}`, ...meta, state: 'running' }])
      }

      const incognito = isIncognitoEnabled()
      // Label depends on the route the message takes. Local LLM is slower so
      // surfacing it early prevents the user thinking it's stuck.
      const thinkingLabel = incognito ? 'Stark réfléchit (incognito)' : 'Stark réfléchit'
      const showThinkingLocal = (): void => {
        setActivities([{ id: 'thinking', kind: 'thinking', label: thinkingLabel, state: 'running' }])
      }
      if (incognito) showThinkingLocal()

      void chatStream(
        {
          message: trimmed,
          ...(sessionId ? { session_id: sessionId } : {}),
          ...(incognito ? { local: true } : {}),
          ...(hasAttachments ? { attachments } : {}),
        },
        {
          signal: ctrl.signal,
          onEvent: (ev: ChatStreamEvent) => {
            switch (ev.type) {
              case 'session':
                currentSessionId = ev.session_id
                if (ev.session_id !== sessionId) {
                  setSessionId(ev.session_id)
                  saveCurrentSessionId(ev.session_id)
                  // First server-side ack of the brand-new session: surface
                  // it in the sidebar Récents block immediately, no refresh
                  // required. Cache key shape mirrors useRecentSessions().
                  void queryClient.invalidateQueries({ queryKey: ['sessions'] })
                }
                break

              // Recall is a quiet background step — don't surface it.
              case 'recall_start':
              case 'recall_end':
                break

              case 'llm_start':
                // Don't replace a tool indicator just because we're between iterations.
                setActivities((prev) =>
                  prev.length === 0 || prev[0]?.kind === 'thinking'
                    ? [{ id: 'thinking', kind: 'thinking', label: thinkingLabel, state: 'running' }]
                    : prev,
                )
                break
              case 'llm_end':
                showThinkingLocal()
                break
              case 'llm_error':
                setError(ev.message || 'Erreur LLM')
                break

              case 'tool_start':
                showTool(ev.name)
                break
              case 'tool_end':
                // Slip back to "réfléchit" — the model is processing the result.
                if (!ev.ok) setError(`Outil ${ev.name} a échoué`)
                showThinkingLocal()
                break

              case 'done': {
                const assistantMsg: StoredMessage = {
                  role: 'assistant',
                  content: ev.response,
                  ...(ev.tool_calls.length > 0
                    ? { toolCalls: ev.tool_calls.map((t) => ({ name: t.name })) }
                    : {}),
                }
                setMessages((prev) => {
                  const next = [...prev, assistantMsg]
                  saveMessages(currentSessionId ?? ev.session_id, next)
                  return next
                })
                if (ev.session_id !== sessionId) {
                  setSessionId(ev.session_id)
                  saveCurrentSessionId(ev.session_id)
                }
                setIsSending(false)
                // Drop activities once the answer lands — keeps the UI clean.
                window.setTimeout(() => setActivities([]), 150)
                // Server has just persisted the assistant turn. Refetch the
                // sessions list so the LLM-generated title (which lands a
                // few hundred ms later in the background) appears in
                // Récents without a manual refresh. We schedule one
                // immediate invalidate plus a delayed one to catch the
                // title once the background generator commits.
                void queryClient.invalidateQueries({ queryKey: ['sessions'] })
                window.setTimeout(() => {
                  void queryClient.invalidateQueries({ queryKey: ['sessions'] })
                }, 2500)
                break
              }
              case 'error': {
                setError(ev.message || 'Erreur')
                setIsSending(false)
                setActivities([])
                break
              }
              case 'turn_start':
                showThinkingLocal()
                break
              case 'turn_end':
                break
            }
          },
        },
      ).catch((err: unknown) => {
        if ((err as { name?: string })?.name === 'AbortError') return
        setError((err as Error)?.message || 'Erreur réseau')
        setIsSending(false)
        setActivities([])
      })
    },
    [sessionId, queryClient],
  )

  const reset = useCallback(() => {
    abortRef.current?.abort()
    clearMessages(sessionId)
    setMessages([])
    setSessionId(null)
    saveCurrentSessionId(null)
    setError(null)
    setActivities([])
    setIsSending(false)
  }, [sessionId])

  const loadSession = useCallback(async (sid: string) => {
    abortRef.current?.abort()
    setError(null)
    setActivities([])
    setIsSending(false)
    // Server is the source of truth for past sessions; localStorage only
    // mirrors the current tab's optimistic state. We hydrate from /api so the
    // user sees the same history they'd see on /sessions/:id.
    const remote = await getSessionMessages(sid)
    const stored: StoredMessage[] = remote
      .filter((m): m is typeof m & { role: 'user' | 'assistant' } =>
        m.role === 'user' || m.role === 'assistant',
      )
      .map((m) => {
        // Surface the persisted attachment manifest as StoredAttachment so
        // image previews come back after a reload. The server URL is treated
        // as the dataUrl: ChatMessage's <img src> + Lightbox both accept it.
        const meta = (m.metadata ?? {}) as { attachments?: Array<{ name: string; mime: string; size: number; url?: string }> }
        const atts = Array.isArray(meta.attachments) ? meta.attachments : []
        const attachments = atts.map((a) => ({
          name: a.name,
          mime: a.mime,
          size: a.size,
          ...(a.url ? { dataUrl: a.url } : {}),
        }))
        return {
          role: m.role,
          content: m.content,
          ...(attachments.length > 0 ? { attachments } : {}),
        }
      })
    setMessages(stored)
    setSessionId(sid)
    saveCurrentSessionId(sid)
    saveMessages(sid, stored)
  }, [])

  const clearError = useCallback(() => setError(null), [])

  return { messages, sessionId, isSending, error, activities, send, reset, clearError, loadSession }
}
